import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { FileText, Download, Calendar, FolderOpen } from 'lucide-react';
import { projectAPI, timesheetAPI } from '../services/api';
import { exportConsolidatedTimesheetPdf } from '../utils/consolidatedTimesheetPdf';
import toast from 'react-hot-toast';

const ConsolidatedTimesheetExport = () => {
  const [selectedProject, setSelectedProject] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // Format dates as YYYY-MM-DD using local date components to avoid timezone issues
  const formatDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Get current week as default (Sunday to Saturday)
  React.useEffect(() => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate Sunday (start of week)
    const daysToSunday = currentDay === 0 ? 0 : -currentDay;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysToSunday);
    sunday.setHours(0, 0, 0, 0);
    
    // Verify it's actually Sunday
    if (sunday.getDay() !== 0) {
      // If not Sunday, adjust to the previous Sunday
      const daysToAdjust = sunday.getDay();
      sunday.setDate(sunday.getDate() - daysToAdjust);
    }
    
    // Calculate Saturday (end of week) - exactly 6 days after Sunday
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    saturday.setHours(0, 0, 0, 0);
    
    // Verify it's actually Saturday
    if (saturday.getDay() !== 6) {
      // If not Saturday, adjust
      const daysToAdjust = 6 - saturday.getDay();
      saturday.setDate(saturday.getDate() + daysToAdjust);
    }

    setStartDate(formatDate(sunday));
    setEndDate(formatDate(saturday));
  }, [formatDate]);

  const { data: projects, isLoading: projectsLoading } = useQuery(
    'all-projects',
    () => projectAPI.getAllProjects(),
    {
      select: (response) => response.data?.projects || [],
    }
  );

  const { data: timesheetsData, isLoading: timesheetsLoading } = useQuery(
    ['timesheets-consolidated', startDate, endDate],
    () => timesheetAPI.getTimesheetsByDateRange(startDate, endDate),
    {
      enabled: !!startDate && !!endDate,
      select: (response) => {
        // Always return all timesheets - filtering by project will happen in PDF
        return { timesheets: response.data?.timesheets || [] };
      },
    }
  );

  const handleThisWeek = () => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate Sunday (start of week)
    const daysToSunday = currentDay === 0 ? 0 : -currentDay;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysToSunday);
    sunday.setHours(0, 0, 0, 0);
    
    // Verify it's actually Sunday
    if (sunday.getDay() !== 0) {
      // If not Sunday, adjust to the previous Sunday
      const daysToAdjust = sunday.getDay();
      sunday.setDate(sunday.getDate() - daysToAdjust);
    }
    
    // Calculate Saturday (end of week) - exactly 6 days after Sunday
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    saturday.setHours(0, 0, 0, 0);
    
    // Verify it's actually Saturday
    if (saturday.getDay() !== 6) {
      // If not Saturday, adjust
      const daysToAdjust = 6 - saturday.getDay();
      saturday.setDate(saturday.getDate() + daysToAdjust);
    }

    setStartDate(formatDate(sunday));
    setEndDate(formatDate(saturday));
  };

  const handleThisMonth = () => {
    const today = new Date();
    
    // First day of current month
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    firstDay.setHours(0, 0, 0, 0);
    
    // Last day of current month
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    lastDay.setHours(0, 0, 0, 0);

    setStartDate(formatDate(firstDay));
    setEndDate(formatDate(lastDay));
  };

  const handleGeneratePDF = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }

    if (!timesheetsData || timesheetsData.timesheets.length === 0) {
      toast.error('No timesheets found for the selected criteria');
      return;
    }

    setIsGenerating(true);
    try {
      const projectName = selectedProject === 'ALL' 
        ? 'All Projects' 
        : projects.find(p => p.id === parseInt(selectedProject))?.name || 'Selected Project';
      
      // Revoke previous URL if exists
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      
      const blobUrl = await exportConsolidatedTimesheetPdf(timesheetsData, {
        projectName,
        startDate,
        endDate,
        selectedProjectId: selectedProject !== 'ALL' ? parseInt(selectedProject) : null,
        returnBlob: true,
      });
      
      setPdfUrl(blobUrl);
      toast.success('Consolidated timesheet PDF generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  // Cleanup blob URL on unmount
  React.useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center space-x-3 mb-6">
          <FileText className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Consolidated Timesheet Export</h1>
            <p className="text-gray-600 mt-1">
              Generate consolidated PDF reports for projects
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FolderOpen className="w-4 h-4 inline mr-1" />
              Select Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={projectsLoading}
            >
              <option value="ALL">All Projects</option>
              {projects?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Date Range
            </label>
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Choose only per month or week
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleThisWeek}
                  className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 hover:border-primary-300 transition-colors"
                >
                  Choose Week (Sun-Sat)
                </button>
                <button
                  type="button"
                  onClick={handleThisMonth}
                  className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 hover:border-primary-300 transition-colors"
                >
                  Choose Month
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {timesheetsData && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              {timesheetsData.timesheets.length} timesheet(s) found for the selected criteria
            </p>
          </div>
        )}

        {/* Generate Button */}
        <div className="mt-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleGeneratePDF}
            disabled={isGenerating || timesheetsLoading || !startDate || !endDate}
            className="w-full md:w-auto px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>{isGenerating ? 'Generating PDF...' : 'Generate Consolidated PDF'}</span>
          </motion.button>
        </div>
      </motion.div>

      {/* PDF Display */}
      {pdfUrl && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Generated PDF</h2>
            <a
              href={pdfUrl}
              download={`Consolidate_Timesheet_${selectedProject === 'ALL' ? 'All_Projects' : projects.find(p => p.id === parseInt(selectedProject))?.name || 'Selected_Project'}_${startDate}.pdf`}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download PDF</span>
            </a>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <iframe
              src={pdfUrl}
              className="w-full"
              style={{ height: '800px' }}
              title="Consolidated Timesheet PDF"
            />
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ConsolidatedTimesheetExport;

